import React, { useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import { toast } from 'react-toastify';
import { X } from 'react-feather';
import Select from 'react-select';
import { parseCurrencyToFloat, displayDecimal } from '../../../../_helper/regexHandler';
import { useModalCrudState } from '../../../../_helper/hooks/useModalCrudState';
import { ENFORCEMENT_STATUSES, ENFORCEMENT_TYPES, TRACKING_DETAIL_TYPES } from '../../../../_helper/schemaAllowedValues';

const EnforcementDataModal = ({
    apiEndpoint,
    isOpen,
    toggle,
    mode,
    selectedEnforcement,
    refreshData,
    arbitrationId,
    isSentenced = true,
    arbitrationData
}) => {
    const initialFormData = useMemo(() => ({
        arbitration: [],
        followUpDate: '',
        enforcementStatus: '',
        foyNumber: '',
        courtOffice: '',
        year: '',
        enforcementCaseNumber: '',
        trackingDetailType: [],
        enforcementType: '',
        debtor: [],
        insuranceCompany: null,
        insuranceCompanyName: '',
        principalClaim: '',
        interestAmount: '',
        attorneyFee: '',
        litigationFee: '',
        totalAmount: '',
        commentType: 'Genel',
        comment: ''
    }), []);

    const validationSchema = useMemo(() => ({
        foyNumber: { required: true, message: 'FOY numarası zorunludur.' },
        courtOffice: { required: true, message: 'İcra dairesi zorunludur.' },
        year: { required: true, message: 'Yıl zorunludur.' },
        enforcementCaseNumber: { required: true, message: 'İcra dosya numarası zorunludur.' },
        followUpDate: { required: true, message: 'Takip tarihi zorunludur.' },
        enforcementType: { required: true, message: 'İcra türü zorunludur.' },
        trackingDetailType: { required: true, message: 'Takip detay türü zorunludur.' },
        enforcementStatus: { required: true, message: 'İcra durumu zorunludur.' },
        principalClaim: { required: true, message: 'Asıl alacak zorunludur.' },
        interestAmount: { required: true, message: 'Faiz tutarı zorunludur.' },
        attorneyFee: { required: true, message: 'Vekalet ücreti zorunludur.' },
        litigationFee: {
            when: () => isSentenced,
            required: true,
            message: 'Yargılama ücreti zorunludur.',
        },
    }), [isSentenced]);

    const {
        formData,
        setFormData,
        errors,
        setErrors,
        handleCancel,
        handleInputChange,
        handleSubmit,
        isSubmitting,
    } = useModalCrudState({
        isOpen,
        mode,
        selectedItem: selectedEnforcement,
        initialData: initialFormData,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => {
            const existingComment = selectedItem?.comments && selectedItem.comments.length > 0
                ? selectedItem.comments[0].comment
                : '';
            const existingCommentType = selectedItem?.comments && selectedItem.comments.length > 0
                ? selectedItem.comments[0].commentType
                : 'Genel';

            return {
                ...initialData,
                ...selectedItem,
                followUpDate: selectedItem?.followUpDate ? new Date(selectedItem.followUpDate).toISOString().split('T')[0] : '',
                year: selectedItem?.year?.toString() || '',
                principalClaim: selectedItem?.principalClaim?.toString() || '',
                interestAmount: selectedItem?.interestAmount?.toString() || '',
                attorneyFee: selectedItem?.attorneyFee?.toString() || '',
                litigationFee: selectedItem?.litigationFee?.toString() || '',
                totalAmount: selectedItem?.totalAmount?.toString() || '',
                trackingDetailType: Array.isArray(selectedItem?.trackingDetailType)
                    ? selectedItem.trackingDetailType
                    : (selectedItem?.trackingDetailType ? [selectedItem.trackingDetailType] : []),
                debtor: selectedItem?.debtor && selectedItem.debtor.length > 0
                    ? selectedItem.debtor
                    : [{ order: 1, name: '' }],
                insuranceCompany: Array.isArray(selectedItem?.insuranceCompany) && selectedItem.insuranceCompany.length > 0
                    ? selectedItem.insuranceCompany[0]
                    : (selectedItem?.insuranceCompany?.id || null),
                insuranceCompanyName: Array.isArray(selectedItem?.insuranceCompany) && selectedItem.insuranceCompany.length > 0
                    ? selectedItem.insuranceCompany[0]?.name || ''
                    : (selectedItem?.insuranceCompany?.name || ''),
                commentType: existingCommentType,
                comment: existingComment,
            };
        },
        normalizeInput: true,
        validationSchema,
        submitConfig: {
            apiEndpoint,
            refreshData,
            componentName: 'İcra',
            buildPayload: (data) => {
                const submitData = {
                    arbitration: Array.isArray(data.arbitration) ? data.arbitration : [arbitrationId],
                    followUpDate: data.followUpDate ? new Date(data.followUpDate).toISOString() : null,
                    enforcementStatus: data.enforcementStatus || null,
                    foyNumber: data.foyNumber || null,
                    courtOffice: data.courtOffice || null,
                    year: data.year ? parseInt(data.year) : 0,
                    enforcementCaseNumber: data.enforcementCaseNumber || null,
                    trackingDetailType: Array.isArray(data.trackingDetailType) && data.trackingDetailType.length > 0
                        ? data.trackingDetailType
                        : [],
                    enforcementType: data.enforcementType || null,
                    principalClaim: parseCurrencyToFloat(data.principalClaim),
                    interestAmount: parseCurrencyToFloat(data.interestAmount),
                    attorneyFee: parseCurrencyToFloat(data.attorneyFee),
                    litigationFee: parseCurrencyToFloat(data.litigationFee, null),
                    comments: data.comment && data.comment.trim() !== '' ? [{
                        commentType: data.commentType || "Genel",
                        comment: data.comment.trim()
                    }] : [],
                };

                if (!isSentenced) {
                    const validDebtors = data.debtor.filter(d => d && d.name && d.name.trim() !== '');
                    submitData.debtor = validDebtors.map((d, idx) => ({
                        order: idx + 1,
                        name: d.name.trim()
                    }));
                }

                if (isSentenced && data.insuranceCompany) {
                    submitData.insuranceCompany = [data.insuranceCompany];
                }

                return submitData;
            },
        },
    });

    const trackingDetailTypeOptions = TRACKING_DETAIL_TYPES.map(type => ({ value: type, label: type }));

    useEffect(() => {
        if (isOpen && mode !== 'modify') {
            let defaultInsuranceCompany = null;
            let insuranceCompanyName = '';

            if (isSentenced && arbitrationData?.accidentFile) {
                const accidentFile = arbitrationData.accidentFile;
                const whoseInsurance = accidentFile.whoseInsurance;

                if (!whoseInsurance) {
                    toast.warning('Kimin Sigortası seçeneği seçilmemiş. Lütfen kaza dosyasında bu alanı doldurunuz.');
                } else {
                    switch (whoseInsurance) {
                        case 'MÜVEKKİL TRAFİK SİGORTA':
                            if (accidentFile.accident?.licensePlate?.trafficInsurancePolicy?.trafficInsuranceProvider) {
                                defaultInsuranceCompany = accidentFile.accident.licensePlate.trafficInsurancePolicy.trafficInsuranceProvider.id;
                                insuranceCompanyName = accidentFile.accident.licensePlate.trafficInsurancePolicy.trafficInsuranceProvider.name;
                            }
                            break;
                        case 'MÜVEKKİL KASKO':
                            if (accidentFile.accident?.licensePlate?.cascoInsurancePolicy?.cascoInsuranceProvider) {
                                defaultInsuranceCompany = accidentFile.accident.licensePlate.cascoInsurancePolicy.cascoInsuranceProvider.id;
                                insuranceCompanyName = accidentFile.accident.licensePlate.cascoInsurancePolicy.cascoInsuranceProvider.name;
                            }
                            break;
                        case 'KARŞI TARAF TRAFİK SİGORTA':
                            if (accidentFile.accident?.counterpartyTrafficInsurancePolicy?.trafficInsuranceProvider) {
                                defaultInsuranceCompany = accidentFile.accident.counterpartyTrafficInsurancePolicy.trafficInsuranceProvider.id;
                                insuranceCompanyName = accidentFile.accident.counterpartyTrafficInsurancePolicy.trafficInsuranceProvider.name;
                            }
                            break;
                        case 'KARŞI TARAF KASKO':
                            if (accidentFile.accident?.counterpartyCascoInsurancePolicy?.cascoInsuranceProvider) {
                                defaultInsuranceCompany = accidentFile.accident.counterpartyCascoInsurancePolicy.cascoInsuranceProvider.id;
                                insuranceCompanyName = accidentFile.accident.counterpartyCascoInsurancePolicy.cascoInsuranceProvider.name;
                            }
                            break;
                        default:
                            break;
                    }

                    if (!defaultInsuranceCompany) {
                        toast.warning(`${whoseInsurance} için sigorta şirketi bilgisi bulunamadı.`);
                    }
                }
            }

            setFormData(prev => ({
                ...prev,
                arbitration: [arbitrationId],
                year: new Date().getFullYear().toString(),
                enforcementType: isSentenced ? 'İlamlı' : 'İlamsız',
                debtor: [{ order: 1, name: '' }],
                insuranceCompany: defaultInsuranceCompany,
                insuranceCompanyName,
            }));
        }
    }, [isOpen, mode, arbitrationId, isSentenced, arbitrationData, setFormData]);

    // Takip detay türü çoklu seçim değerlerini forma yazar
    const handleTrackingDetailTypeChange = (selectedOptions) => {
        const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
        setFormData(prev => ({ ...prev, trackingDetailType: values }));
        if (setErrors) {
            setErrors(prev => (prev.trackingDetailType ? { ...prev, trackingDetailType: null } : prev));
        }
    };

    // Borçlu listesine yeni satır ekler
    const addDebtor = () => {
        const newOrder = formData.debtor.length + 1;
        setFormData(prev => ({
            ...prev,
            debtor: [...prev.debtor, { order: newOrder, name: '' }]
        }));
    };

    // Borçlu listesinden satır siler ve sıra numaralarını düzenler
    const removeDebtor = (index) => {
        if (formData.debtor.length === 1) {
            toast.warning('En az bir borçlu bulunmalıdır');
            return;
        }

        const newDebtors = formData.debtor.filter((_, i) => i !== index);
        const reorderedDebtors = newDebtors.map((debtor, idx) => ({
            ...debtor,
            order: idx + 1
        }));

        setFormData(prev => ({
            ...prev,
            debtor: reorderedDebtors
        }));
    };

    // Borçlu adı değişikliklerini ilgili satıra uygular
    const handleDebtorChange = (index, value) => {
        const newDebtors = [...formData.debtor];
        newDebtors[index] = { ...newDebtors[index], name: value };
        setFormData(prev => ({
            ...prev,
            debtor: newDebtors
        }));
        if (setErrors) {
            setErrors(prev => (prev.debtor ? { ...prev, debtor: null } : prev));
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} size="xl">
            <ModalHeader toggle={handleCancel}>
                {mode === 'add' ? 'İcra Ekle' : 'İcra Düzenle'}
            </ModalHeader>
            <form>
                <ModalBody>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="foyNumber">FOY Numarası <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="foyNumber"
                                    name="foyNumber"
                                    value={formData.foyNumber}
                                    onChange={handleInputChange}
                                    placeholder="FOY numarasını giriniz"
                                    invalid={!!errors.foyNumber}
                                />
                                <FormFeedback>{errors.foyNumber}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="courtOffice">İcra Dairesi <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="courtOffice"
                                    name="courtOffice"
                                    value={formData.courtOffice}
                                    onChange={handleInputChange}
                                    placeholder="İcra dairesini giriniz"
                                    invalid={!!errors.courtOffice}
                                />
                                <FormFeedback>{errors.courtOffice}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="year">Yıl <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="year"
                                    name="year"
                                    value={formData.year}
                                    onChange={handleInputChange}
                                    placeholder="Yıl"
                                    maxLength="4"
                                    invalid={!!errors.year}
                                />
                                <FormFeedback>{errors.year}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="enforcementCaseNumber">İcra Dosya Numarası <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="enforcementCaseNumber"
                                    name="enforcementCaseNumber"
                                    value={formData.enforcementCaseNumber}
                                    onChange={handleInputChange}
                                    placeholder="Dosya numarasını giriniz"
                                    invalid={!!errors.enforcementCaseNumber}
                                />
                                <FormFeedback>{errors.enforcementCaseNumber}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="followUpDate">Takip Tarihi <span className="text-danger">*</span></Label>
                                <Input
                                    type="date"
                                    id="followUpDate"
                                    name="followUpDate"
                                    value={formData.followUpDate}
                                    onChange={handleInputChange}
                                    invalid={!!errors.followUpDate}
                                />
                                <FormFeedback>{errors.followUpDate}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="enforcementType">İcra Türü <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    id="enforcementType"
                                    name="enforcementType"
                                    value={formData.enforcementType}
                                    onChange={handleInputChange}
                                    invalid={!!errors.enforcementType}
                                >
                                    <option value="">Seçiniz...</option>
                                    {ENFORCEMENT_TYPES.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </Input>
                                <FormFeedback>{errors.enforcementType}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="trackingDetailType">Takip Detay Türü <span className="text-danger">*</span></Label>
                                <Select
                                    isMulti
                                    id="trackingDetailType"
                                    name="trackingDetailType"
                                    options={trackingDetailTypeOptions}
                                    value={trackingDetailTypeOptions.filter(option =>
                                        formData.trackingDetailType.includes(option.value)
                                    )}
                                    onChange={handleTrackingDetailTypeChange}
                                    placeholder="Seçiniz..."
                                    classNamePrefix="select"
                                    noOptionsMessage={() => "Seçenek bulunamadı"}
                                />
                                {errors.trackingDetailType && (
                                    <small className="text-danger">{errors.trackingDetailType}</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="enforcementStatus">İcra Durumu <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    id="enforcementStatus"
                                    name="enforcementStatus"
                                    value={formData.enforcementStatus}
                                    onChange={handleInputChange}
                                    invalid={!!errors.enforcementStatus}
                                >
                                    <option value="">Seçiniz...</option>
                                    {ENFORCEMENT_STATUSES.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </Input>
                                <FormFeedback>{errors.enforcementStatus}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="principalClaim">Asıl Alacak (₺) <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="principalClaim"
                                    name="principalClaim"
                                    value={displayDecimal(formData.principalClaim)}
                                    onChange={handleInputChange}
                                    placeholder="Örn: 1000,50"
                                    invalid={!!errors.principalClaim}
                                />
                                <FormFeedback>{errors.principalClaim}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="interestAmount">Faiz Tutarı (₺) <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="interestAmount"
                                    name="interestAmount"
                                    value={displayDecimal(formData.interestAmount)}
                                    onChange={handleInputChange}
                                    placeholder="Örn: 1000,50"
                                    invalid={!!errors.interestAmount}
                                />
                                <FormFeedback>{errors.interestAmount}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="attorneyFee">Vekalet Ücreti (₺) <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="attorneyFee"
                                    name="attorneyFee"
                                    value={displayDecimal(formData.attorneyFee)}
                                    onChange={handleInputChange}
                                    placeholder="Örn: 1000,50"
                                    invalid={!!errors.attorneyFee}
                                />
                                <FormFeedback>{errors.attorneyFee}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        {isSentenced && (
                            <Col md={4}>
                                <FormGroup>
                                    <Label for="litigationFee">Yargılama Ücreti (₺) <span className="text-danger">*</span></Label>
                                    <Input
                                        type="text"
                                        id="litigationFee"
                                        name="litigationFee"
                                        value={displayDecimal(formData.litigationFee)}
                                        onChange={handleInputChange}
                                        placeholder="Örn: 1000,50"
                                        invalid={!!errors.litigationFee}
                                    />
                                    <FormFeedback>{errors.litigationFee}</FormFeedback>
                                    </FormGroup>
                            </Col>
                        )}
                        <Col md={4}>
                            <FormGroup>
                                <Label for="totalAmount">Toplam Tutar (₺)</Label>
                                <Input
                                    type="text"
                                    id="totalAmount"
                                    name="totalAmount"
                                    value={formData.totalAmount ? new Intl.NumberFormat('tr-TR', {
                                        style: 'currency',
                                        currency: 'TRY'
                                    }).format(parseFloat(formData.totalAmount)) : '₺0,00'}
                                    disabled={true}
                                    style={{ backgroundColor: '#e9ecef', color: '#212529' }}
                                />
                                <small className="text-muted">Bu alan otomatik olarak hesaplanır (Salt Okunur)</small>
                            </FormGroup>
                        </Col>
                    </Row>

                    <hr className="my-4" />

                    {!isSentenced && (
                        <Row>
                            <Col md={12}>
                                <Label className="mb-3">Borçlular</Label>

                                {formData.debtor.map((debtor, index) => (
                                    <Row key={index} className="mb-2 align-items-center">
                                        <Col md={1}>
                                            <div className="text-center fw-bold">{debtor.order}.</div>
                                        </Col>
                                        <Col md={10}>
                                            <Input
                                                type="text"
                                                value={debtor.name}
                                                onChange={(e) => handleDebtorChange(index, e.target.value)}
                                                placeholder="Borçlu adı soyadı"
                                            />
                                        </Col>
                                        <Col md={1} className="d-flex gap-1">
                                            {formData.debtor.length > 1 && (
                                                <Button
                                                    outline
                                                    color="danger"
                                                    size="sm"
                                                    type="button"
                                                    onClick={() => removeDebtor(index)}
                                                    className="p-2"
                                                    title="Borçluyu Sil"
                                                >
                                                    <X size={16} />
                                                </Button>
                                            )}
                                        </Col>
                                    </Row>
                                ))}

                                <Row className="mt-2">
                                    <Col md={12} className="text-center">
                                        <Button
                                            outline
                                            color="success"
                                            type="button"
                                            onClick={addDebtor}
                                            className="p-2 mt-2"
                                            title="Borçlu Ekle"
                                        >
                                            Borçlu Ekle
                                        </Button>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    )}

                    {isSentenced && (
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label for="insuranceCompanyName">
                                        Sigorta Şirketi
                                    </Label>
                                    <Input
                                        type="text"
                                        id="insuranceCompanyName"
                                        name="insuranceCompanyName"
                                        value={formData.insuranceCompanyName || 'Sigorta şirketi bilgisi bulunamadı'}
                                        disabled={true}
                                        className={!formData.insuranceCompany ? 'text-danger' : ''}
                                    />
                                    <small className="text-muted">
                                        Sigorta şirketi otomatik olarak kaza dosyasının "Kimin Sigortası" alanına göre atanmıştır.
                                    </small>
                                </FormGroup>
                            </Col>
                        </Row>
                    )}

                    <hr className="my-4" />

                    <FormGroup>
                        <Label for="comment">Yorum</Label>
                        <Input
                            type="textarea"
                            rows="4"
                            id="comment"
                            name="comment"
                            value={formData.comment}
                            onChange={handleInputChange}
                            placeholder="İcra ile ilgili yorumunuzu yazınız..."
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={handleCancel} disabled={isSubmitting}>İptal</Button>
                    <Button color="primary" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                </ModalFooter>
            </form>
        </Modal>
    );
};

export default EnforcementDataModal;
